import React, { useCallback } from 'react';
import { NodeType, ImageGenerationMode } from '@/types';
import { Palette, MapPin, Focus, VideoIcon, Users } from 'lucide-react';
import { InputAsset } from '../../hooks/useInputAssets';

interface ModeTab {
  id: string;
  label: string;
}

const MODE_TABS: Record<NodeType, ModeTab[]> = {
  [NodeType.VIDEO_GENERATOR]: [
    { id: 'txt2video', label: '文生视频' },
    { id: 'omni', label: '全能参考' },
    { id: 'img2video', label: '图生视频' },
    { id: 'first_last', label: '首尾帧' },
    { id: 'img_ref', label: '图片参考' },
  ],
  [NodeType.IMAGE_GENERATOR]: [
    { id: 'txt2img', label: '文生图' },
    { id: 'img2img', label: '图生图' },
    { id: 'upscale', label: '高清重绘' },
    { id: 'style', label: '风格迁移' },
  ],
  [NodeType.AUDIO_GENERATOR]: [
    { id: 'tts', label: '文字转语音' },
    { id: 'audio2video', label: '音频生视频' },
  ],
  [NodeType.PROMPT_INPUT]: [
    { id: 'write', label: '自己编写' },
    { id: 'txt2video', label: '文生视频' },
    { id: 'img2prompt', label: '图片反推' },
    { id: 'txt2music', label: '文字生音乐' },
  ],
  [NodeType.VIDEO_ANALYZER]: [{ id: 'analyze', label: '视频分析' }],
  [NodeType.IMAGE_EDITOR]: [{ id: 'edit', label: '图像编辑' }],
  [NodeType.MEDIA_UPLOAD]: [],
  [NodeType.TEXT_NODE]: [],
  [NodeType.SCRIPT_NODE]: [],
  [NodeType.VIDEO_COMPOSE]: [],
  [NodeType.PANORAMA_GENERATOR]: [
    { id: 'txt2panorama', label: '文生全景' },
    { id: 'img2panorama', label: '图生全景' },
  ],
  [NodeType.GROUP]: [],
};

const IMAGE_MODE_MAP: Record<string, ImageGenerationMode> = {
  'txt2img': 'TXT2IMG',
  'img2img': 'IMG2IMG',
  'upscale': 'UPSCALE',
  'style': 'STYLE',
};

const IMAGE_MODE_TO_TAB: Record<string, string> = {
  'TXT2IMG': 'txt2img',
  'IMG2IMG': 'img2img',
  'UPSCALE': 'upscale',
  'STYLE': 'style',
};

const placeholders: Record<NodeType, string> = {
  [NodeType.IMAGE_GENERATOR]: '描述你想要生成的画面内容，按/呼出指令，@引用素材',
  [NodeType.VIDEO_GENERATOR]: '描述你想要生成的视频内容...',
  [NodeType.AUDIO_GENERATOR]: '描述你想生成的音乐或音效...',
  [NodeType.PROMPT_INPUT]: '写下你想讲的故事、场景或角色设定。例如：一个来自未来的机器人，在城市屋顶看星星。',
  [NodeType.VIDEO_ANALYZER]: '描述分析需求...',
  [NodeType.IMAGE_EDITOR]: '描述你的修改需求...',
  [NodeType.MEDIA_UPLOAD]: '',
  [NodeType.TEXT_NODE]: '',
  [NodeType.SCRIPT_NODE]: '',
  [NodeType.VIDEO_COMPOSE]: '',
  [NodeType.PANORAMA_GENERATOR]: '描述你想要的全景画面，例如"生成一张科技展厅的720°全景图"，支持上传场景参考图。',
  [NodeType.GROUP]: '',
};

const imageModePlaceholders: Record<string, string> = {
  'TXT2IMG': '描述你想要生成的画面内容，按/呼出指令，@引用素材',
  'IMG2IMG': '描述你想要生成的画面，参考图将作为基础...',
  'UPSCALE': '描述增强需求，如"提升清晰度、补充细节"...',
  'STYLE': '描述想要的风格，参考图将作为内容基础...',
};

interface PromptEditorProps {
  nodeId: string;
  nodeType: NodeType;
  prompt: string;
  currentImageMode?: ImageGenerationMode;
  inputAssets: InputAsset[];
  isWorking: boolean;
  currentMode: string;
  onPromptChange: (value: string) => void;
  onPromptSubmit: () => void;
  onImageModeSwitch?: (mode: ImageGenerationMode) => void;
  onAddUploadNode?: () => void;
  onUpdate: (id: string, data: any) => void;
  onSetCurrentMode: (mode: string) => void;
  onSetIsInputFocused: (v: boolean) => void;
  onGenerate: () => void;
}

export const PromptEditor: React.FC<PromptEditorProps> = ({
  nodeId,
  nodeType,
  prompt,
  currentImageMode,
  inputAssets,
  isWorking,
  currentMode,
  onPromptChange,
  onPromptSubmit,
  onImageModeSwitch,
  onAddUploadNode,
  onUpdate,
  onSetCurrentMode,
  onSetIsInputFocused,
  onGenerate,
}) => {
  const isUpscaleMode = nodeType === NodeType.IMAGE_GENERATOR && currentImageMode === 'UPSCALE';
  const availableModes = MODE_TABS[nodeType] || [];
  const activeMode = nodeType === NodeType.IMAGE_GENERATOR
    ? (IMAGE_MODE_TO_TAB[currentImageMode || 'TXT2IMG'] || 'txt2img')
    : nodeType === NodeType.PANORAMA_GENERATOR
      ? (currentMode || (inputAssets.length > 0 ? 'img2panorama' : 'txt2panorama'))
      : (currentMode || (availableModes[0]?.id || ''));

  const handleModeTabClick = useCallback((modeTab: ModeTab) => {
    if (nodeType === NodeType.IMAGE_GENERATOR && onImageModeSwitch) {
      const targetImageMode = IMAGE_MODE_MAP[modeTab.id];
      if (targetImageMode) {
        onImageModeSwitch(targetImageMode);
      }
    } else if (nodeType === NodeType.PANORAMA_GENERATOR) {
      if (modeTab.id === 'img2panorama' && inputAssets.length === 0 && onAddUploadNode) {
        onAddUploadNode();
      }
      onSetCurrentMode(modeTab.id);
    } else {
      onSetCurrentMode(modeTab.id);
    }
  }, [nodeType, onImageModeSwitch, inputAssets.length, onAddUploadNode, onSetCurrentMode]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
      e.preventDefault();
      onPromptSubmit();
      onGenerate();
    }
  }, [onPromptSubmit, onGenerate]);

  const handlePromptBlur = useCallback(() => {
    onSetIsInputFocused(false);
    onPromptSubmit();
  }, [onPromptSubmit, onSetIsInputFocused]);

  const getImagePlaceholder = () => {
    if (nodeType === NodeType.IMAGE_GENERATOR && currentImageMode) {
      return imageModePlaceholders[currentImageMode] || imageModePlaceholders['TXT2IMG'];
    }
    return placeholders[nodeType] || '描述您的生成需求...';
  };

  return (
    <>
      {availableModes.length > 0 && !isUpscaleMode && (
        <div className="flex items-center gap-2 flex-wrap">
          {availableModes.map((modeTab) => {
            const isActive = activeMode === modeTab.id;
            const isVideoNode = nodeType === NodeType.VIDEO_GENERATOR;
            const isPanoramaNode = nodeType === NodeType.PANORAMA_GENERATOR;
            const hasInput = inputAssets.length > 0;
            let isDisabled = false;
            if (nodeType === NodeType.IMAGE_GENERATOR && modeTab.id === 'txt2img' && hasInput) {
              isDisabled = true;
            } else if (isVideoNode) {
              if (!hasInput && modeTab.id !== 'txt2video') isDisabled = true;
              else if (hasInput && modeTab.id === 'txt2video') isDisabled = true;
            } else if (isPanoramaNode) {
              if (hasInput && modeTab.id === 'txt2panorama') isDisabled = true;
            }
            return (
              <button
                key={modeTab.id}
                onClick={() => !isDisabled && handleModeTabClick(modeTab)}
                className={`px-3 py-1 rounded-lg text-xs transition-all duration-200 ${
                  isDisabled
                    ? 'text-white/15 cursor-not-allowed'
                    : isActive
                      ? 'bg-white/10 text-white/90 font-medium'
                      : 'text-white/40 hover:text-white/60 hover:bg-white/5'
                }`}
              >
                {modeTab.label}
              </button>
            );
          })}
        </div>
      )}

      {!(nodeType === NodeType.IMAGE_GENERATOR && currentImageMode === 'TXT2IMG') && (
        <div className="flex items-center gap-2">
          {nodeType === NodeType.IMAGE_GENERATOR && (
            <>
              <button className="flex flex-col items-center justify-center gap-1 w-9 h-9 rounded-lg border border-white/10 text-white/50 hover:text-white hover:bg-white/5 hover:border-white/20 transition-all flex-shrink-0" title="风格"><Palette size={14} /><span className="text-[9px] leading-none">风格</span></button>
              <button className="flex flex-col items-center justify-center gap-1 w-9 h-9 rounded-lg border border-white/10 text-white/50 hover:text-white hover:bg-white/5 hover:border-white/20 transition-all flex-shrink-0" title="标记"><MapPin size={14} /><span className="text-[9px] leading-none">标记</span></button>
              <button className="flex flex-col items-center justify-center gap-1 w-9 h-9 rounded-lg border border-white/10 text-white/50 hover:text-white hover:bg-white/5 hover:border-white/20 transition-all flex-shrink-0" title="聚焦"><Focus size={14} /><span className="text-[9px] leading-none">聚焦</span></button>
            </>
          )}
          {nodeType === NodeType.VIDEO_GENERATOR && (
            <>
              <button className="flex flex-col items-center justify-center gap-1 w-9 h-9 rounded-lg border border-white/10 text-white/50 hover:text-white hover:bg-white/5 hover:border-white/20 transition-all flex-shrink-0" title="标记"><MapPin size={14} /><span className="text-[9px] leading-none">标记</span></button>
              <button className="flex flex-col items-center justify-center gap-1 w-9 h-9 rounded-lg border border-white/10 text-white/50 hover:text-white hover:bg-white/5 hover:border-white/20 transition-all flex-shrink-0" title="运镜"><VideoIcon size={14} /><span className="text-[9px] leading-none">运镜</span></button>
              <button className="flex flex-col items-center justify-center gap-1 w-9 h-9 rounded-lg border border-white/10 text-white/50 hover:text-white hover:bg-white/5 hover:border-white/20 transition-all flex-shrink-0" title="角色库"><Users size={14} /><span className="text-[9px] leading-none">角色库</span></button>
            </>
          )}
          {nodeType === NodeType.PANORAMA_GENERATOR && (
            <>
              <button className="flex flex-col items-center justify-center gap-1 w-9 h-9 rounded-lg border border-white/10 text-white/50 hover:text-white hover:bg-white/5 hover:border-white/20 transition-all flex-shrink-0" title="风格"><Palette size={14} /><span className="text-[9px] leading-none">风格</span></button>
              <button className="flex flex-col items-center justify-center gap-1 w-9 h-9 rounded-lg border border-white/10 text-white/50 hover:text-white hover:bg-white/5 hover:border-white/20 transition-all flex-shrink-0" title="标记"><MapPin size={14} /><span className="text-[9px] leading-none">标记</span></button>
            </>
          )}
          {inputAssets.length > 0 && inputAssets.slice(0, 4).map((asset, index) => (
            <div key={asset.id} className="relative w-9 h-9 rounded-lg overflow-hidden border border-white/20 flex-shrink-0">
              {asset.type === 'video' ? (
                <video src={asset.src} className="w-full h-full object-cover" muted loop autoPlay />
              ) : (
                <img src={asset.src} className="w-full h-full object-cover" alt={`参考 ${index + 1}`} />
              )}
              <div className="absolute -top-1 -right-1 w-3.5 h-3.5 bg-black/60 rounded-full flex items-center justify-center">
                <span className="text-[7px] font-bold text-white">{index + 1}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {!isUpscaleMode && (
        <div className="relative group/input bg-white/[0.03] rounded-xl border border-white/[0.06] focus-within:border-white/[0.15] transition-colors">
          <textarea
            className="w-full bg-transparent text-[13px] text-slate-200 placeholder-white/20 p-3 focus:outline-none resize-none font-medium leading-relaxed overflow-y-auto panel-scrollbar"
            style={{ height: '80px', minHeight: '80px', maxHeight: '120px' }}
            placeholder={getImagePlaceholder()}
            value={prompt}
            onChange={(e) => onPromptChange(e.target.value)}
            onBlur={handlePromptBlur}
            onFocus={() => onSetIsInputFocused(true)}
            onKeyDown={handleKeyDown}
            onMouseDown={(e) => e.stopPropagation()}
            readOnly={isWorking}
          />
        </div>
      )}
    </>
  );
};
